import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useScreenName from "../../hooks/useScreenName";
import useQueryParams from "../../hooks/useQueryParams";
import {
  PolicyAttachmentTimelineEvent,
  UnderwritingPolicyAttachmentStatus,
} from "../../models/underwritingPolicyAttachment";
import dispatch from "../../middleware";
import { getPolicyAttachmentTimeline } from "../../actions/policy-attachment.action";
import ActiveSnapshotBanner from "./ActiveSnapshotBanner";
import TimelineView from "./TimelineView";
import "./timelineStyles.css";

type QueryParamsType = {
  employerId: string;
};

const DEFAULT_QUERY_PARAMS: QueryParamsType = {
  employerId: "",
};

export default function PolicyAttachmentTimelinePage() {
  useScreenName("Underwriting");

  const storeDispatch = useDispatch();
  const [queryParams] = useQueryParams<QueryParamsType>(DEFAULT_QUERY_PARAMS);
  const [events, setEvents] = useState<PolicyAttachmentTimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  async function fetchTimeline() {
    if (!queryParams.employerId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await dispatch(
        storeDispatch,
        getPolicyAttachmentTimeline(queryParams.employerId)
      );

      const metadataMap = new Map<
        string,
        PolicyAttachmentTimelineEvent["applicabilityMetadata"]
      >();

      const processedEvents = response.events.reduce((acc: PolicyAttachmentTimelineEvent[], event: PolicyAttachmentTimelineEvent) => {
        if (event.status === UnderwritingPolicyAttachmentStatus.SHADOW) {
          if (event.applicabilityMetadata) {
            metadataMap.set(event.attachmentId, event.applicabilityMetadata);
          }
          acc.push(event);
          return acc;
        }

        if (
          event.status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT &&
          !event.applicabilityMetadata &&
          metadataMap.has(event.attachmentId)
        ) {
          acc.push({
            ...event,
            applicabilityMetadata: metadataMap.get(event.attachmentId),
          });
        } else {
          acc.push(event);
        }

        return acc;
      }, [] as PolicyAttachmentTimelineEvent[]);

      setEvents(processedEvents);
    } catch (error) {
      console.error("Error fetching timeline:", error);
      alert("Error fetching policy attachment timeline");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.employerId]);

  if (!queryParams.employerId) {
    return (
      <div>
        <div className="dashboard-container">
          <p className="grey-text">
            No employer ID provided. Please select an employer from the Policy
            Attachments list.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Timeline for EmployerId: {queryParams.employerId}</h2>
      </div>

      <div className="dashboard-container">
        {isLoading ? (
          <div className="rf-timeline-empty">Loading timeline...</div>
        ) : (
          <>
            <ActiveSnapshotBanner events={events} />
            <TimelineView events={events} />
          </>
        )}
      </div>
    </div>
  );
}
